import { faUpload } from '@fortawesome/free-solid-svg-icons'
import { updateCustomerCA } from 'features/settings'
import { Button, IconButton, Label, TextArea, Tooltip, Typography } from 'procyon-ui'
import React, { useEffect, useRef, useState } from 'react'
import { enqueueNotification } from 'Utils/Helpers'
import { CopyText, FullScreenContentModal, LoadingFeedback, Wizard } from 'V2Components'

function UploadUserCAModal({ onCancel, csr }) {
  const [rootCertificate, setRootCertificate] = useState('')
  const [signedCertificate, setSignedCertificate] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const rootFileInputRef = useRef(null)
  const signedFileInputRef = useRef(null)

  useEffect(() => {
    console.log('rootCertificate changed:', rootCertificate.length)
  }, [rootCertificate])

  const FooterComponent = ({ activeStep, setActiveStep, totalSteps }) => {
    const handleNext = async () => {
      if (activeStep === totalSteps - 1) {
        try {
          setIsUpdating(true)
          await updateCustomerCA({
            csr,
            rootCertificate,
            signedCertificate
          })

          enqueueNotification('Customer CA updated successfully', 'info')
          onCancel()
        } catch (error) {
          const errorMessage =
            error.response?.data?.error ||
            error.response?.data?.message ||
            'Failed to update Customer CA'
          enqueueNotification(errorMessage, 'error')
        } finally {
          setIsUpdating(false)
          onCancel()
        }
      } else {
        setActiveStep(activeStep + 1)
      }
    }

    const handlePrevious = () => {
      setActiveStep(activeStep - 1)
    }

    return (
      <div className='flex items-center justify-end gap-2 mt-5'>
        {getErrors() && getErrors().errorStep === activeStep && (
          <Label variant='warning' text={getErrors().message} />
        )}
        <Button disabled={activeStep === 0} onClick={handlePrevious} variant='gray'>
          Previous
        </Button>
        <Button
          disabled={!!getErrors() && getErrors().errorStep === activeStep}
          onClick={handleNext}
        >
          {activeStep === totalSteps - 1 ? 'Submit' : 'Next'}
        </Button>
      </div>
    )
  }

  const getErrors = () => {
    // begins with 0 as first step
    let errorStep = 1

    if (!rootCertificate.trim()) return { errorStep, message: 'Root Certificate is required' }
    if (!signedCertificate.trim()) return { errorStep, message: 'Signed Certificate is required' }

    return null
  }

  const handleUploadCertificate = async (e, type) => {
    const file = e.target.files?.[0]
    if (!file) {
      console.warn('No file selected')
      return
    }
    try {
      const text = await file.text()
      if (type === 'rootCert') {
        setRootCertificate(text)
      } else {
        setSignedCertificate(text)
      }
    } catch (err) {
      console.error('Error reading certificate file:', err)
    } finally {
      // reset input so same file selection possible
      e.target.value = ''
    }
  }

  return (
    <FullScreenContentModal>
      <div>
        <div>
          <Wizard
            onCancel={onCancel}
            FooterComponent={FooterComponent}
            header='Upload User CA'
            steps={[
              {
                label: 'Verify',
                content: (
                  <div>
                    <Typography variant='body-regular'>
                      Run the following command to verify the certificate signing.
                    </Typography>
                    <CopyText
                      Component={
                        <div className='mt-2'>
                          <Tooltip title='Click to Copy' arrow>
                            <div className='p-2 rounded bg-gray-100 cursor-copy'>
                              <Typography className='!break-all' variant='body-regular'>
                                <code>
                                  openssl verify -CAfile &lt;root-cert-file&gt;
                                  signed-certificate.crt
                                </code>
                              </Typography>
                            </div>
                          </Tooltip>
                        </div>
                      }
                      messageKey={'Signing Command'}
                      text={`openssl verify -CAfile <root-cert-file> signed-certificate.crt`}
                    />
                    <Typography className='mt-4' variant='body-regular'>
                      The command should output
                    </Typography>
                    <div className='p-2 mt-2 rounded bg-gray-100'>
                      <Typography className='!break-all' variant='body-regular'>
                        <code>signed-certificate.crt: OK</code>
                      </Typography>
                    </div>
                  </div>
                )
              },
              {
                label: 'Upload Certificates',
                content: (
                  <div>
                    <div className='mb-4 relative'>
                      <div className='mb-2'>
                        <div className='absolute right-[1%] top-[25%]'>
                          <IconButton
                            onClick={() => rootFileInputRef.current?.click()}
                            title='Upload'
                            icon={faUpload}
                            variant='gray'
                          />

                          <input
                            type='file'
                            ref={rootFileInputRef}
                            accept='.crt,.cer,.pem,.txt,.csr'
                            className='hidden'
                            onChange={(e) => handleUploadCertificate(e, 'rootCert')}
                          />
                        </div>
                        <div>
                          <TextArea
                            key={rootCertificate}
                            value={rootCertificate}
                            onChange={(e) => setRootCertificate(e.target.value)}
                            width='100%'
                            label='Root Certificate'
                            placeholder='Root Certificate'
                            rows={5}
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className='mb-4 relative'>
                        <div className='mb-2'>
                          <div className='absolute right-[1%] top-[25%]'>
                            <IconButton
                              onClick={() => signedFileInputRef.current?.click()}
                              title='Upload'
                              icon={faUpload}
                              variant='gray'
                            />

                            <input
                              type='file'
                              ref={signedFileInputRef}
                              accept='.crt,.cer,.pem,.txt,.csr'
                              className='hidden'
                              onChange={(e) => handleUploadCertificate(e, 'signedCert')}
                            />
                          </div>
                          <TextArea
                            key={signedCertificate}
                            value={signedCertificate}
                            onChange={(e) => setSignedCertificate(e.target.value)}
                            width='100%'
                            label='Signed Certificate'
                            placeholder='The Signed Certificate'
                            rows={5}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }
            ]}
          />
        </div>
        <LoadingFeedback
          loading={isUpdating}
          message='Updating Customer CA'
          caption='Please wait...'
        />
      </div>
    </FullScreenContentModal>
  )
}

export default UploadUserCAModal
